import type { PlanTapelineConfig, PlanType } from 'src/domain/planTables/types'
import { commentColorOptions } from './commentColors'

/**
 * Explicit comment type, persisted on the comment (API `type` field).
 *
 * Legacy comments persisted before the type field existed are treated as
 * `custom` (see `getCommentType` / `FALLBACK_COMMENT_TYPE`).
 *
 * For the three non-custom types the `color` / `backgroundColor` / `showInPlans`
 * are derived from the type (see `commentTypePresets`) and are not edited
 * manually – only `custom` exposes the advanced color / "In Plänen anzeigen"
 * controls.
 */
export type CommentType =
  | 'sticky-note'
  | 'stiffening-plan'
  | 'position-plan'
  | 'load-plan'
  | 'custom'

/** The non-custom types, whose appearance/plan-visibility is derived. */
export type DerivedCommentType = Exclude<CommentType, 'custom'>

export const commentTypes: CommentType[] = [
  'sticky-note',
  'stiffening-plan',
  'position-plan',
  'load-plan',
  'custom',
]

/** Type assigned to comments newly created through the in-scene tooling. */
export const DEFAULT_NEW_COMMENT_TYPE: DerivedCommentType = 'position-plan'

/** Fallback for comments persisted before the explicit type field existed. */
export const FALLBACK_COMMENT_TYPE: CommentType = 'custom'

/** Full labels, used in the comment edit popup's type selector. */
export const commentTypeLabels: Record<CommentType, string> = {
  'sticky-note': 'Notiz',
  'stiffening-plan': 'Aussteifungsplan',
  'position-plan': 'Positionsplan',
  'load-plan': 'Lastplan',
  custom: 'Legacy',
}

export interface ShowInPlansConfig {
  positionPlan: boolean
  stiffeningPlan: boolean
  loadPlan: boolean
}

/**
 * The export plans a comment can be shown on. Comments are filtered in the
 * storey controls by plan membership (see `showInPlans`), not by type.
 */
export type CommentPlan = keyof ShowInPlansConfig

/** Plan filter order in the comment filter dropdown: Aussteifung, Positionsplan, Lastplan. */
export const commentPlans: CommentPlan[] = ['stiffeningPlan', 'positionPlan', 'loadPlan']

/** Plan category shown by default in the comment filter. */
export const DEFAULT_COMMENT_PLAN: CommentPlan = 'positionPlan'

/** Labels for the comment plan-filter dropdown options. */
export const commentPlanLabels: Record<CommentPlan, string> = {
  stiffeningPlan: 'Aussteifungsplan',
  positionPlan: 'Positionsplan',
  loadPlan: 'Lastplan',
}

/**
 * The "no plan" filter category: comments shown on no plan (Notiz, or a custom
 * comment without plan flags). Not a `CommentPlan`/`showInPlans` key – it's the
 * dropdown selection used to view exactly those comments.
 */
export const COMMENT_FILTER_NOTIZ = 'notiz'

/** The "all" filter category: every comment, regardless of plan or type. */
export const COMMENT_FILTER_ALL = 'all'

/**
 * A comment-filter dropdown selection: "all", one of the export plans, or the
 * Notiz/plan-less category. The filter shows one category at a time.
 */
export type CommentPlanFilter =
  | CommentPlan
  | typeof COMMENT_FILTER_NOTIZ
  | typeof COMMENT_FILTER_ALL

/** Option order in the comment filter dropdown (Alle, then plans, then Notiz). */
export const commentPlanFilters: CommentPlanFilter[] = [
  COMMENT_FILTER_ALL,
  ...commentPlans,
  COMMENT_FILTER_NOTIZ,
]

/** Labels for the comment filter dropdown options. */
export const commentPlanFilterLabels: Record<CommentPlanFilter, string> = {
  ...commentPlanLabels,
  [COMMENT_FILTER_NOTIZ]: 'Notiz',
  [COMMENT_FILTER_ALL]: 'Alle',
}

/** Default comment filter — show all comments. */
export const DEFAULT_COMMENT_FILTER: CommentPlanFilter = COMMENT_FILTER_ALL

/** Plan-visibility with nothing shown – the default for new/blank comments. */
export const DEFAULT_SHOW_IN_PLANS: ShowInPlansConfig = {
  positionPlan: false,
  stiffeningPlan: false,
  loadPlan: false,
}

interface CommentTypePreset {
  color: string
  backgroundColor: string
  showInPlans: ShowInPlansConfig
}

const colorOptionByKey = (key: string) => {
  const option = commentColorOptions.find(o => o.key === key)
  if (!option) {
    throw new Error(`commentTypes: expected color option "${key}" in commentColorOptions`)
  }
  return option
}

/**
 * Canonical color + plan-visibility presets for the non-custom types. Selecting
 * one of these types derives these fields onto the comment; `custom` keeps the
 * user's manually-set values. The hex values live in a single place
 * (commentColors.ts).
 */
export const commentTypePresets: Record<DerivedCommentType, CommentTypePreset> = {
  'sticky-note': {
    color: colorOptionByKey('sticky-note').color,
    backgroundColor: colorOptionByKey('sticky-note').backgroundColor,
    showInPlans: { positionPlan: false, stiffeningPlan: false, loadPlan: false },
  },
  'stiffening-plan': {
    color: colorOptionByKey('blue').color,
    backgroundColor: colorOptionByKey('blue').backgroundColor,
    showInPlans: { positionPlan: false, stiffeningPlan: true, loadPlan: false },
  },
  'position-plan': {
    color: colorOptionByKey('red').color,
    backgroundColor: colorOptionByKey('red').backgroundColor,
    showInPlans: { positionPlan: true, stiffeningPlan: false, loadPlan: false },
  },
  'load-plan': {
    color: colorOptionByKey('blue').color,
    backgroundColor: colorOptionByKey('blue').backgroundColor,
    showInPlans: { positionPlan: false, stiffeningPlan: false, loadPlan: true },
  },
}

export const isDerivedCommentType = (type: CommentType): type is DerivedCommentType =>
  type !== 'custom'

export interface CommentTypeCapabilities {
  /** color / backgroundColor, scene-rendering storey and title are user-editable. */
  appearance: boolean
  /** The "In Plänen anzeigen" flags are user-editable. */
  showInPlans: boolean
}

/**
 * Which comment fields the user may edit for a given type. Everything that is
 * not editable is derived from the type (see `commentTypePresets`). Centralised
 * here so the popup doesn't branch on individual type literals.
 */
export const getCommentTypeCapabilities = (type: CommentType): CommentTypeCapabilities => ({
  appearance: type === 'custom',
  showInPlans: type === 'custom' || type === 'sticky-note',
})

/**
 * The persisted (explicit) comment type, falling back to `custom` for legacy
 * comments that predate the type field.
 */
export const getCommentType = (comment: Pick<Comment3D, 'type'>): CommentType =>
  comment.type ?? FALLBACK_COMMENT_TYPE

/**
 * The plan-filter category a comment belongs to: the first plan (in
 * `commentPlans` order) its `showInPlans` flags it for, or null when it is shown
 * on no plan (e.g. Notiz, or a custom comment without plan flags – those are
 * always visible regardless of the filter).
 */
export const getCommentPlan = (comment: Pick<Comment3D, 'showInPlans'>): CommentPlan | null =>
  commentPlans.find(plan => comment.showInPlans?.[plan]) ?? null

/**
 * The comment-filter dropdown category a comment belongs to. Notiz comments
 * (type `sticky-note`) and comments shown on no plan map to the Notiz category;
 * everything else maps to its first plan (see `getCommentPlan`). Type takes
 * precedence over `showInPlans` so a Notiz comment that still carries a plan
 * flag is categorised as Notiz, not by that flag.
 */
export const getCommentFilterCategory = (
  comment: Pick<Comment3D, 'type' | 'showInPlans'>,
): CommentPlanFilter =>
  getCommentType(comment) === 'sticky-note'
    ? COMMENT_FILTER_NOTIZ
    : getCommentPlan(comment) ?? COMMENT_FILTER_NOTIZ

/** Maps a comment plan-filter plan to the plan-table / plan-image `PlanType`. */
const commentPlanToPlanType: Record<CommentPlan, PlanType> = {
  stiffeningPlan: 'StiffeningPlan',
  positionPlan: 'PositionPlan',
  loadPlan: 'LoadPlan',
}

/**
 * Whether a plan-bound item (plan table / plan image, via its `planConfig`) is
 * shown under the current comment plan filter. Mirrors comment filtering: "Alle"
 * shows everything, a plan category shows only items assigned to that plan, and
 * "Notiz" (a comment-only category) shows none.
 */
export const isPlanConfigVisibleForCommentFilter = (
  planConfig: PlanTapelineConfig,
  filter: CommentPlanFilter,
): boolean => {
  if (filter === COMMENT_FILTER_ALL) return true
  if (filter === COMMENT_FILTER_NOTIZ) return false
  return planConfig.planTypes.includes(commentPlanToPlanType[filter])
}

const commentPlanToNewCommentType: Record<CommentPlan, DerivedCommentType> = {
  stiffeningPlan: 'stiffening-plan',
  positionPlan: 'position-plan',
  loadPlan: 'load-plan',
}

/**
 * Type of a newly placed comment: the one matching the filtered category, so the comment belongs
 * to what the filter shows (Notiz under "Notiz"); the default type under "Alle".
 */
export const getNewCommentTypeForCommentFilter = (
  filter: CommentPlanFilter,
): DerivedCommentType => {
  if (filter === COMMENT_FILTER_ALL) return DEFAULT_NEW_COMMENT_TYPE
  if (filter === COMMENT_FILTER_NOTIZ) return 'sticky-note'
  return commentPlanToNewCommentType[filter]
}

/**
 * Flags a new plan table / plan image for the plan the comment filter currently shows (and for
 * the given plan storey), so it stays visible under that filter right after it is placed — e.g.
 * on the "Planansicht", which always filters to the previewed plan. "Alle" and "Notiz" leave the
 * config unchanged.
 */
export const withCommentFilterPlan = (
  planConfig: PlanTapelineConfig,
  filter: CommentPlanFilter,
  storey: string | null,
): PlanTapelineConfig => {
  if (filter === COMMENT_FILTER_ALL || filter === COMMENT_FILTER_NOTIZ) return planConfig
  const planType = commentPlanToPlanType[filter]
  return {
    planTypes: planConfig.planTypes.includes(planType)
      ? planConfig.planTypes
      : [...planConfig.planTypes, planType],
    storeysForPlans:
      storey === null || planConfig.storeysForPlans.includes(storey)
        ? planConfig.storeysForPlans
        : [...planConfig.storeysForPlans, storey],
  }
}

/**
 * Whether a comment is shown under the current comment-filter category. Mirrors
 * `CommentsController`'s `visibleComments`: "Alle" shows everything, "Notiz"
 * shows Notiz/plan-less comments (see `getCommentFilterCategory`), and a plan
 * category shows comments flagged for that plan (a Notiz comment can also be
 * flagged for a plan, so it can appear under both filters).
 */
export const isCommentVisibleForCommentFilter = (
  comment: Pick<Comment3D, 'type' | 'showInPlans'>,
  filter: CommentPlanFilter,
): boolean => {
  if (filter === COMMENT_FILTER_ALL) return true
  if (filter === COMMENT_FILTER_NOTIZ)
    return getCommentFilterCategory(comment) === COMMENT_FILTER_NOTIZ
  return !!comment.showInPlans?.[filter]
}

/**
 * Whether a comment is shown given the currently visible storeys. A comment
 * without any assigned storey (`storeysForPlans` empty) always shows; otherwise
 * it shows when at least one of its storeys is visible.
 */
export const isCommentVisibleForStoreys = (
  comment: Pick<Comment3D, 'storeysForPlans'>,
  visibleStoreys: ReadonlySet<string>,
): boolean => {
  const storeys = comment.storeysForPlans ?? []
  if (storeys.length === 0) return true
  return storeys.some(storey => visibleStoreys.has(storey))
}
